import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, ArrowUpRight, X, Eye, Code, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { adminCreateBlog, adminUpdateBlog, uploadBlogCover, mediaUrl } from "@/lib/api";
import { ADMIN_BLOG } from "@/constants/testIds";

const EASE = [0.76, 0, 0.24, 1];

const formatDetail = (d) => {
  if (!d) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" · ");
  return String(d);
};

export default function BlogEditorModal({ open, initial, onClose, onSaved }) {
  const isEdit = Boolean(initial);
  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [body, setBody] = useState("");
  const [tags, setTags] = useState("");
  const [cover, setCover] = useState("");
  const [author, setAuthor] = useState("Orphion");
  const [isPublished, setIsPublished] = useState(true);
  const [loading, setLoading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [error, setError] = useState("");
  const [mobileTab, setMobileTab] = useState("edit"); // edit | preview
  const coverInputRef = useRef(null);

  const pickCover = () => coverInputRef.current?.click();

  const onCoverPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting same file
    if (!file) return;
    setUploadingCover(true);
    try {
      const res = await uploadBlogCover(file);
      setCover(res.url); // backend returns /api/media/...
      toast.success("Cover uploaded.");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Upload failed.";
      toast.error(typeof msg === "string" ? msg : "Upload failed.");
    } finally {
      setUploadingCover(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setTitle(initial.title || "");
      setExcerpt(initial.excerpt || "");
      setBody(initial.body || "");
      setTags((initial.tags || []).join(", "));
      setCover(initial.cover_image || "");
      setAuthor(initial.author || "Orphion");
      setIsPublished(!!initial.is_published);
    } else {
      setTitle("");
      setExcerpt("");
      setBody("# Heading\n\nStart writing…");
      setTags("");
      setCover("");
      setAuthor("Orphion");
      setIsPublished(true);
    }
    setError("");
    setMobileTab("edit");
  }, [open, initial]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = {
        title: title.trim(),
        excerpt: excerpt.trim(),
        body,
        cover_image: cover.trim() || null,
        author: author.trim() || "Orphion",
        tags: tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        is_published: isPublished,
      };
      const saved = isEdit
        ? await adminUpdateBlog(initial.id, payload)
        : await adminCreateBlog(payload);
      toast.success(isEdit ? "Post updated." : "Post created.");
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      setError(formatDetail(err?.response?.data?.detail) || err?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[70] bg-[rgba(20,17,13,0.55)] backdrop-blur-sm flex items-start justify-center px-3 py-6 md:py-10 overflow-y-auto"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="relative bg-[var(--bg)] border border-[var(--line-strong)] w-full max-w-6xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              data-testid={ADMIN_BLOG.editorCancel}
              className="absolute top-4 right-4 text-[var(--ink-70)] hover:text-[var(--ink)] p-1 z-10"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="px-6 md:px-8 pt-6 pb-4 border-b border-[var(--line-strong)]">
              <p className="overline mb-2">
                Insights · {isEdit ? "Edit essay" : "New essay"}
              </p>
              <input
                required
                type="text"
                className="w-full font-display text-2xl md:text-3xl tracking-[-0.01em] text-[var(--ink)] bg-transparent border-0 focus:outline-none placeholder:text-[var(--ink-50)]"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Untitled essay"
                data-testid={ADMIN_BLOG.editorTitle}
              />
            </div>

            <form onSubmit={onSubmit}>
              {/* Meta row */}
              <div className="grid md:grid-cols-3 gap-4 px-6 md:px-8 py-4 border-b border-[var(--line-strong)]">
                <div>
                  <label className="overline block mb-2">Author</label>
                  <input
                    type="text"
                    className="editorial-input !py-2"
                    value={author}
                    onChange={(e) => setAuthor(e.target.value)}
                    data-testid={ADMIN_BLOG.editorAuthor}
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Tags (comma-separated)</label>
                  <input
                    type="text"
                    className="editorial-input !py-2"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="hiring, culture"
                    data-testid={ADMIN_BLOG.editorTags}
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Cover image</label>
                  <div className="flex items-stretch gap-2">
                    <input
                      type="url"
                      className="editorial-input !py-2 flex-1"
                      value={cover}
                      onChange={(e) => setCover(e.target.value)}
                      placeholder="Paste URL or upload"
                      data-testid={ADMIN_BLOG.editorCover}
                    />
                    <button
                      type="button"
                      onClick={pickCover}
                      disabled={uploadingCover}
                      className="btn-ghost text-xs px-3 shrink-0 disabled:opacity-60"
                      title="Upload from your device"
                      data-testid="admin-blog-editor-cover-upload"
                    >
                      {uploadingCover ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <UploadCloud className="w-3.5 h-3.5" />
                      )}
                    </button>
                    <input
                      ref={coverInputRef}
                      type="file"
                      accept="image/*"
                      onChange={onCoverPicked}
                      className="hidden"
                    />
                  </div>
                  {cover && (
                    <img
                      src={mediaUrl(cover)}
                      alt="Cover preview"
                      className="mt-2 w-full max-h-28 object-cover border border-[var(--line)]"
                    />
                  )}
                </div>
              </div>

              <div className="px-6 md:px-8 py-4 border-b border-[var(--line-strong)]">
                <label className="overline block mb-2">Excerpt</label>
                <input
                  required
                  type="text"
                  className="editorial-input !py-2"
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  maxLength={500}
                  placeholder="One-sentence summary for cards and social."
                  data-testid={ADMIN_BLOG.editorExcerpt}
                />
              </div>

              {/* Mobile tabs */}
              <div className="md:hidden flex border-b border-[var(--line-strong)]">
                <button
                  type="button"
                  onClick={() => setMobileTab("edit")}
                  className={`flex-1 py-3 text-xs uppercase tracking-[0.22em] flex items-center justify-center gap-2 ${
                    mobileTab === "edit"
                      ? "text-[var(--ink)] bg-[var(--bg-2)]"
                      : "text-[var(--ink-70)]"
                  }`}
                >
                  <Code className="w-3.5 h-3.5" /> Editor
                </button>
                <button
                  type="button"
                  onClick={() => setMobileTab("preview")}
                  className={`flex-1 py-3 text-xs uppercase tracking-[0.22em] flex items-center justify-center gap-2 ${
                    mobileTab === "preview"
                      ? "text-[var(--ink)] bg-[var(--bg-2)]"
                      : "text-[var(--ink-70)]"
                  }`}
                >
                  <Eye className="w-3.5 h-3.5" /> Preview
                </button>
              </div>

              <div className="grid md:grid-cols-2 min-h-[420px]">
                <div
                  className={`p-6 md:p-8 border-b md:border-b-0 md:border-r border-[var(--line-strong)] ${
                    mobileTab === "preview" ? "hidden md:block" : ""
                  }`}
                >
                  <label className="overline block mb-3 hidden md:flex items-center gap-2">
                    <Code className="w-3.5 h-3.5" /> Markdown
                  </label>
                  <textarea
                    required
                    className="w-full h-[420px] bg-[var(--bg-2)] border border-[var(--line-strong)] p-4 text-sm text-[var(--ink)] font-mono leading-relaxed resize-y focus:outline-none focus:border-[var(--accent)]"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder={"# Heading\n\nWrite with Markdown…"}
                    data-testid={ADMIN_BLOG.editorBody}
                  />
                </div>
                <div
                  className={`p-6 md:p-8 bg-[var(--bg)] ${
                    mobileTab === "edit" ? "hidden md:block" : ""
                  }`}
                >
                  <label className="overline mb-3 hidden md:flex items-center gap-2">
                    <Eye className="w-3.5 h-3.5" /> Live preview
                  </label>
                  <article className="prose prose-sm max-w-none prose-headings:font-display prose-headings:tracking-tight prose-headings:text-[var(--ink)] prose-p:text-[var(--ink)] prose-p:leading-relaxed prose-a:text-[var(--accent)] prose-strong:text-[var(--ink)] prose-li:text-[var(--ink)] prose-code:text-[var(--accent)]">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{body || "*(empty)*"}</ReactMarkdown>
                  </article>
                </div>
              </div>

              <div className="px-6 md:px-8 py-4 border-t border-[var(--line-strong)] flex items-center justify-between gap-4 flex-wrap">
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <span
                    className={`inline-block w-10 h-6 border ${
                      isPublished
                        ? "bg-[var(--accent)] border-[var(--accent)]"
                        : "border-[var(--line-strong)] bg-[var(--bg-2)]"
                    } relative transition-colors`}
                  >
                    <span
                      className={`absolute top-0.5 w-4 h-4 bg-white transition-all ${
                        isPublished ? "left-5" : "left-0.5"
                      }`}
                    />
                  </span>
                  <input
                    type="checkbox"
                    checked={isPublished}
                    onChange={(e) => setIsPublished(e.target.checked)}
                    className="sr-only"
                    data-testid={ADMIN_BLOG.editorPublished}
                  />
                  <span className="text-xs uppercase tracking-[0.22em] text-[var(--ink-70)]">
                    {isPublished ? "Published" : "Draft"}
                  </span>
                </label>

                {error && (
                  <div
                    className="text-red-600 text-sm"
                    data-testid={ADMIN_BLOG.editorError}
                  >
                    {error}
                  </div>
                )}

                <div className="flex items-center gap-4 ml-auto">
                  <button
                    type="button"
                    onClick={onClose}
                    className="text-sm uppercase tracking-[0.22em] text-[var(--ink-70)] hover:text-[var(--ink)] link-slide"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-pill disabled:opacity-60"
                    data-testid={ADMIN_BLOG.editorSave}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Saving…</span>
                      </>
                    ) : (
                      <>
                        <span>{isEdit ? "Save essay" : "Publish essay"}</span>
                        <ArrowUpRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
